import dayjs from 'dayjs';
import React from 'react';

import { Badge, Button, Table, Text } from '@mantine/core';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { restrictionApi } from '@/shared/api/restriction';
import { IRestriction } from '@/shared/types/restriction.types';

import { IconCheck, IconReload, IconX } from '@tabler/icons-react';

type RestrictionsItemProps = {
	restriction: IRestriction;
	someCarPass: boolean;
};

const restrictionStatuses: Record<IRestriction['state'], Record<string, string>> = {
	waiting: {
		label: 'На рассмотрении',
		color: 'yellow',
	},
	approved: {
		label: 'Подтверждено',
		color: 'red',
	},
	declined: {
		label: 'Отклонено',
		color: 'green',
	},
};

const RestrictionsItem: React.FC<RestrictionsItemProps> = ({ restriction, someCarPass }) => {
	const queryClient = useQueryClient();

	const { mutate: updateRestriction, isPending: isUpdatingRestriction } = useMutation({
		mutationFn: restrictionApi.updateRestriction,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['restrictions'] }),
	});

	const payload = {
		car_number: restriction.car_number,
		booking_id: restriction.booking_id,
		person_id: restriction.person_id,
		fixation_date: restriction.fixation_date,
		id: restriction.id,
	};

	return (
		<Table.Tr key={restriction.id}>
			<Table.Td>
				<Text size="md" fw={600} lts="normal">
					{restriction.person?.full_name}
				</Text>
			</Table.Td>
			<Table.Td>
				<Text size="md" c="dimmed">
					{restriction.booking?.booking_object.name}
				</Text>
			</Table.Td>
			<Table.Td ta="center">
				{someCarPass && (
					<Badge variant="light">
						<Text size="md" fw={600}>
							{restriction?.car_number}
						</Text>
					</Badge>
				)}
			</Table.Td>
			<Table.Td ta="center">
				<Text size="md" c="dimmed">
					{dayjs(restriction.booking?.start).format('DD MMMM YYYY HH:mm')}
				</Text>
			</Table.Td>
			<Table.Td ta="center">
				<Text size="md" c="dimmed">
					{dayjs(restriction.booking?.end).format('DD MMMM YYYY HH:mm')}
				</Text>
			</Table.Td>
			<Table.Td ta="center">
				<Badge variant="light" color={restrictionStatuses[restriction?.state].color}>
					<Text size="sm" fw={600}>
						{restrictionStatuses[restriction?.state].label}
					</Text>
				</Badge>
			</Table.Td>
			<Table.Td ta="center">
				{restriction?.state === 'waiting' ? (
					<Button.Group>
						<Button
							variant="light"
							size="compact-sm"
							radius="sm"
							onClick={() => {
								updateRestriction({
									id: restriction.id,
									payload: { ...payload, state: 'approved' },
								});
							}}
							loading={isUpdatingRestriction}
						>
							<IconCheck size={16} />
						</Button>
						<Button
							variant="light"
							size="compact-sm"
							radius="sm"
							color="red"
							onClick={() => {
								updateRestriction({
									id: restriction.id,
									payload: { ...payload, state: 'declined' },
								});
							}}
							loading={isUpdatingRestriction}
						>
							<IconX size={16} />
						</Button>
					</Button.Group>
				) : (
					<Button
						variant="light"
						size="compact-sm"
						radius="sm"
						color="yellow"
						onClick={() =>
							updateRestriction({
								id: restriction.id,
								payload: { ...payload, state: 'waiting' },
							})
						}
						loading={isUpdatingRestriction}
					>
						<IconReload size={16} />
					</Button>
				)}
			</Table.Td>
		</Table.Tr>
	);
};

export default RestrictionsItem;
